/* ==========================================================================
   Nordenheim — bundle builder (Duo / Trio / Collector)
   --------------------------------------------------------------------------
   A custom element, so the theme editor is handled for free: connectedCallback
   fires whenever the element enters the document (including on a section
   re-render) and disconnectedCallback tears the listeners down again, with no
   shopify:section:* plumbing.

   State is ONE ordered set — up to N chosen plates, oldest first — held on the
   instance. That is the whole reason this is a separate file from plate-snap
   rather than a mode of it: plate-snap holds a single current plate as a pair
   of scalars, and every method that touches selection would have had to fork.

   `derive()` below is a VERBATIM copy of the one in
   assets/nordenheim-plate-snap.js. It is copied rather than imported so the two
   sections cannot break each other, and so nothing about plate-snap's behaviour
   changes to accommodate this one. If the snap geometry is ever corrected, both
   copies must move together — that is the price of the isolation.
   ========================================================================== */

/* THE SHARED HALF (round 18), from assets/nordenheim-bundle-core.js. Read off
   the global rather than imported: the section emits that file as a deferred
   classic script through `asset_url`, so it carries the `?v=` a relative ESM
   specifier would have dropped — see the note at the head of that file. A
   deferred script and a module execute in document order, so it is here. */
const NH_CORE = window.NordenheimBundleCore || {};
const { installBundleCore, clamp, cssUrl, fill, thumbSrc } = NH_CORE;

const TAG = 'nordenheim-bundle-builder';

/* ROUND 19 — INFO_MIN_H and cssAttr() moved to assets/nordenheim-bundle-core.js
   with the info popover and the cap control. Nothing left in this file reads
   either of them; leaving dead copies behind is how two definitions of one
   number come to disagree. */

/**
 * Derive every geometry value from one plate rectangle.
 *
 * A cap colourway supplies only where the plate sits on its mockup
 * ({top,left,width,height} as % of the square image). From that we derive:
 *
 *   clip  — the region of the incoming mockup to reveal. Padded outward,
 *           because the pixels just outside the plate are identical between
 *           mockups of the same colourway, so over-clipping is invisible while
 *           under-clipping shaves the plate edge mid-animation.
 *   thumb — background-size/position that frames the plate inside a thumbnail,
 *           reusing the full mockup instead of a separate cropped asset.
 */
function derive(rect, thumbAspect, padPercent, vRatio, focus, picFrac) {
  const top = Number(rect.top) || 0;
  const left = Number(rect.left) || 0;
  const width = Number(rect.width) || 0;
  const height = Number(rect.height) || 0;
  const pad = Number(padPercent) || 0;

  // THE STAGE IS A LANDSCAPE WINDOW ONTO A SQUARE COMPOSITE (round 7, Sina),
  // AND SINCE ROUND 10 THE SQUARE MAY BE NARROWER THAN THE WINDOW.
  //
  // Two numbers describe where the picture is, both measured from the live box
  // rather than assumed (see stageGeom()):
  //
  //   vRatio  = the displayed square's SIDE / the box's height. 1 / vRatio is
  //             the fraction of the composite the box shows vertically.
  //   picFrac = the displayed square's SIDE / the box's width, so 1 while the
  //             picture fills the box and less than 1 once a short viewport has
  //             capped the box's height and the picture has narrowed instead.
  //
  // `plate_rect` is a percentage of the SQUARE, so both halves have to be
  // re-expressed as percentages of the BOX or the clip lands off the plate:
  //
  //     top_box    = top_img * vRatio - (vRatio - 1) * f
  //     height_box = height_img * vRatio
  //     left_box   = (1 - picFrac) / 2 + left_img * picFrac
  //     width_box  = width_img * picFrac
  //
  // f is where the vertical crop is anchored — `background-position`'s vertical
  // half as a fraction, read from --nh-bb-stage-focus so the stylesheet and this
  // arithmetic cannot disagree. It is NOT 0.5: the band is anchored to hold the
  // whole cap (13.4%..90% of every composite), not to centre the square.
  //
  // The pad scales with vRatio on the vertical axis and picFrac on the
  // horizontal one, so it stays the same number of PIXELS on both.
  //
  // vRatio = 1 and picFrac = 1 collapse every term back to the original
  // arithmetic, which is what keeps this a superset of the plate-snap copy
  // rather than a fork of it.
  const r = Number(vRatio) > 0 ? Number(vRatio) : 1;
  const f = Number.isFinite(Number(focus)) ? Number(focus) : 0.5;
  const k = Number(picFrac) > 0 ? Math.min(1, Number(picFrac)) : 1;
  const topBox = top * r - (r - 1) * f * 100;
  const heightBox = height * r;
  const padY = pad * r;
  const leftBox = (1 - k) * 50 + left * k;
  const widthBox = width * k;
  const padX = pad * k;

  const clip = {
    top: Math.max(0, topBox - padY),
    right: Math.max(0, 100 - (leftBox + widthBox) - padX),
    bottom: Math.max(0, 100 - (topBox + heightBox) - padY),
    left: Math.max(0, leftBox - padX),
  };

  const pw = width / 100;
  const cx = (left + width / 2) / 100;
  const cy = (top + height / 2) / 100;
  const a = Number(thumbAspect) || 10 / 7;

  let size = 100;
  let posX = 50;
  let posY = 50;
  const denomX = pw - 1;
  const denomY = 1 / a - 1 / pw;
  if (pw > 0 && Math.abs(denomX) > 1e-6 && Math.abs(denomY) > 1e-6) {
    size = 100 / pw;
    posX = ((0.5 * pw - cx) / denomX) * 100;
    posY = ((1 / (2 * a) - cy / pw) / denomY) * 100;
  }

  return {
    clip: `inset(${clip.top.toFixed(2)}% ${clip.right.toFixed(2)}% ${clip.bottom.toFixed(
      2
    )}% ${clip.left.toFixed(2)}% round 5%)`,
    thumb: {
      size: clamp(size, 100, 2000),
      x: clamp(posX, -200, 300),
      y: clamp(posY, -200, 300),
    },
  };
}
/* The stage's layers carry their image as a CUSTOM PROPERTY, not as
   background-image: the stylesheet paints the same URL TWICE — the picture at
   --nh-bb-pic-w over the same photograph at `cover`, so that whatever shows
   beside a narrowed picture is that picture's own ground rather than a mat.
   One assignment, two layers, one cached request. */
function setLayerImg(el, src) {
  if (!el) return;
  el.style.setProperty('--nh-bb-layer-img', `url("${cssUrl(src)}")`);
}

function stageSrc(plate, capKey) {
  const entry = plate && plate.images && plate.images[capKey];
  if (!entry) return '';
  return typeof entry === 'string' ? entry : entry.stage || '';
}
class NordenheimBundleBuilder extends HTMLElement {
  connectedCallback() {
    if (this.ready || this.initIo) return;

    // The build parses the inline payload and paints the first stage image, and
    // on a bundle PDP this section is at the top of the page — but the payload
    // can be a few hundred KB, so it still waits for the element to approach the
    // viewport rather than competing with the page's own first paint. The margin
    // is generous enough that on a normal load it has already run by the time
    // anyone looks at it.
    if ('IntersectionObserver' in window) {
      this.initIo = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.initIo.disconnect();
          this.initIo = null;
          this.build();
        },
        { rootMargin: '600px 0px' }
      );
      this.initIo.observe(this);
    } else {
      this.build();
    }
  }

  disconnectedCallback() {
    if (this.initIo) this.initIo.disconnect();
    this.initIo = null;
    if (this.stageRo) this.stageRo.disconnect();
    this.stageRo = null;
    if (this.stickRo) this.stickRo.disconnect();
    this.stickRo = null;
    if (this.railRo) this.railRo.disconnect();
    this.railRo = null;
    if (this.compactIo) this.compactIo.disconnect();
    this.compactIo = null;
    if (this.barRo) this.barRo.disconnect();
    this.barRo = null;
    if (this.awayIo) this.awayIo.disconnect();
    this.awayIo = null;
    // ROUND 15.1: an open sheet had made the rest of the page inert; a section
    // removed mid-open must not leave the page that way.
    this.setPageInert(false);
    if (!this.ready) return;
    this.ready = false;
    (this.listeners || []).forEach(([t, type, fn, opts]) => t.removeEventListener(type, fn, opts));
    this.listeners = [];
    (this.timers || new Set()).forEach((t) => clearTimeout(t));
    this.timers = new Set();
    if (this.mq && this.mqHandler) {
      if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.mqHandler);
      else if (this.mq.removeListener) this.mq.removeListener(this.mqHandler);
    }
  }

  /* ---------------------------------------------------------------- build */

  build() {
    if (this.ready) return;

    const data = this.readData();
    if (!data) return;

    this.data = data;
    this.settings = data.settings || {};
    this.labels = this.settings.labels || {};
    this.max = Math.max(1, Number(this.getAttribute('data-plates')) || Number(this.settings.plates) || 1);
    // Two independent pieces of state, and keeping them apart is the whole of
    // Sina's 2026-09-02 change: `previewed` is what the stage is showing,
    // `chosen` is what is being bought. Before, one click did both.
    this.chosen = [];
    this.previewed = null;
    // -1 is the live combination; anything else indexes shotsFor(previewed).
    this.shotIndex = -1;
    // Viewing and buying are separate (Sina, 2026-09-02). `capKey` is the
    // colourway being BOUGHT — the radio row decides it, and it decides the
    // variant. `viewCap` is the colourway on the STAGE, which the gallery
    // strip's on-cap thumbnails move. They start equal and only diverge if you
    // ask them to.
    this.viewCap = null;
    this.listeners = [];
    this.timers = new Set();

    const q = (sel) => this.querySelector(sel);
    this.els = {
      stage: q('[data-nh-bb-stage]'),
      base: q('[data-nh-bb-base]'),
      caption: q('[data-nh-bb-caption]'),
      capNote: q('[data-nh-bb-cap-note]'),
      subline: q('[data-nh-bb-subline]'),
      tray: q('[data-nh-bb-tray]'),
      count: q('[data-nh-bb-count]'),
      // ROUND 16 (Sina, item 11) — the hint line is gone; these two are its
      // replacement, a floating confirmation anchored above the bar/deck.
      toast: q('[data-nh-bb-toast]'),
      toastText: q('[data-nh-bb-toast-text]'),
      // ROUND 12: the pre-order line under the button, and the hidden input that
      // carries the per-add group id on the BUNDLE line.
      prenote: q('[data-nh-bb-prenote]'),
      group: q('[data-nh-bb-group]'),
      form: q('[data-nh-bb-form]'),
      variant: q('[data-nh-bb-variant]'),
      button: q('[data-nh-bb-button]'),
      label: q('[data-nh-bb-label]'),
      price: q('[data-nh-bb-price]'),
      name: q('[data-nh-bb-name]'),
      status: q('[data-nh-bb-status]'),
      error: q('[data-nh-bb-error]'),
      wrapper: q('[data-nh-bb-wrapper]'),
      capName: q('[data-nh-bb-cap-name]'),
      capAffix: q('[data-nh-bb-cap-affix]'),
      capBtnThumb: q('[data-nh-bb-capbtn-thumb]'),
      capCombo: q('[data-nh-bb-capcombo]'),
      trayBlock: q('[data-nh-bb-trayblock]'),
      trayTitle: q('.nh-bb__traytitle'),
      strip: q('[data-nh-bb-strip]'),
      stageAdd: q('[data-nh-bb-stage-add]'),
      stageAddLabel: q('[data-nh-bb-stage-add-label]'),
      buy: q('.nh-bb__buy'),
      bar: q('[data-nh-bb-bar]'),
      deck: q('[data-nh-bb-deck]'),
      panel: q('.nh-bb__panel'),
      view: q('.nh-bb__view'),
      capline: q('.nh-bb__capline'),
      info: q('[data-nh-bb-info]'),
      infoName: q('[data-nh-bb-info-name]'),
      pop: q('[data-nh-bb-pop]'),
      popTitle: q('[data-nh-bb-pop-title]'),
      popCollab: q('[data-nh-bb-pop-collab]'),
      popBody: q('[data-nh-bb-pop-body]'),
      popLink: q('[data-nh-bb-pop-link]'),
      // OUTSIDE this custom element: the compact header is a child of the
      // SECTION, so that no ancestor's overflow can clip a fixed box (see the
      // Liquid). `this.querySelector` cannot see it — the same reason the
      // picker's rows are found on the document.
      stickyHead: document.querySelector('[data-nh-bb-stickyhead]'),
      viewSentinel: q('[data-nh-bb-viewsentinel]'),
      endSentinel: q('[data-nh-bb-endsentinel]'),
      capModal: q('[data-nh-bb-capmodal]'),
      capModalBody: q('[data-nh-bb-capmodal-body]'),
      catwrap: q('[data-nh-bb-catwrap]'),
      catsentinel: q('[data-nh-bb-catsentinel]'),
      cats: q('[data-nh-bb-tablist]'),
    };
    // ROUND 15 — THE TRAY SHEET. Like the compact header, the dock is a child
    // of the SECTION (so no ancestor's overflow can clip a fixed box), which
    // puts it outside this element: it is found on the section, not on `this`
    // and not on the document, so two builders on one page cannot cross.
    const sec = this.closest('.nh-bb');
    const rq = (sel) => (sec ? sec.querySelector(sel) : null);
    Object.assign(this.els, {
      dock: rq('[data-nh-bb-dock]'),
      scrim: rq('[data-nh-bb-scrim]'),
      sheet: rq('[data-nh-bb-sheet]'),
      sheetGrip: rq('[data-nh-bb-sheet-grip]'),
      sheetBody: rq('[data-nh-bb-sheet-body]'),
      sheetStage: rq('[data-nh-bb-sheet-stage]'),
      sheetCapline: rq('[data-nh-bb-sheet-capline]'),
      sheetAdd: rq('[data-nh-bb-sheet-add]'),
      sheetAddLabel: rq('[data-nh-bb-sheet-add-label]'),
      sheetAddName: rq('[data-nh-bb-sheet-add-name]'),
      // sheetCap / sheetCapGrid — ROUND 16: REMOVED with the "Your cap" grid.
      sheetStrip: rq('[data-nh-bb-sheet-strip]'),
      sheetFoot: rq('[data-nh-bb-sheetfoot]'),
    });
    if (!this.els.stage || !this.els.base) return;
    // The cap LISTBOX, held directly. The Tray Sheet moves it out of the combo
    // and into the sheet's cap grid (round 15), so `capCombo.querySelector`
    // stops finding it — every reader of it goes through this reference.
    this.els.capList = this.els.capCombo
      ? this.els.capCombo.querySelector('[data-nh-bb-combo-list]')
      : null;
    // Where the bar goes back to when the sheet lets go of it (a resize past
    // 901px). Remembered once, here, before anything moves.
    if (this.els.bar) {
      this.barHome = this.els.bar.parentNode;
      this.barNext = this.els.bar.nextSibling;
    }
    // ROUND 15.1: the cap tile's popup semantics. In the sheet it opens a
    // DIALOG (the sheet), not a listbox; the desktop attributes are kept so
    // placeSheet can hand them back above 901px.
    this.els.capBtn = this.els.capCombo
      ? this.els.capCombo.querySelector('[data-nh-bb-combo-btn]')
      : null;
    if (this.els.capBtn) {
      this.capBtnPopup = {
        haspopup: this.els.capBtn.getAttribute('aria-haspopup') || 'listbox',
        controls: this.els.capBtn.getAttribute('aria-controls') || '',
      };
    }

    this.tabs = Array.from(this.querySelectorAll('[data-nh-bb-tab]'));
    this.catPanels = Array.from(this.querySelectorAll('[data-nh-bb-catpanel]'));
    this.previewButtons = Array.from(this.querySelectorAll('[data-nh-bb-preview]'));
    this.confirms = Array.from(this.querySelectorAll('[data-nh-bb-confirm]'));
    // ROUND 15: the form's per-slot hidden inputs, held by reference. The Tray
    // Sheet moves the bar — and the form in it — into the dock, which is
    // outside this element, so a `this.querySelector` at sync time finds
    // nothing and every plate line ships disabled (measured: a Trio posted as
    // one line). Captured here, before anything moves.
    this.itemInputs = [];
    for (let i = 1; i <= this.max; i += 1) {
      this.itemInputs[i] = {
        id: q(`[data-nh-bb-item="${i}"]`),
        qty: q(`[data-nh-bb-item-qty="${i}"]`),
        grp: q(`[data-nh-bb-item-group="${i}"]`),
        part: q(`[data-nh-bb-item-part="${i}"]`),
        pre: q(`[data-nh-bb-item-pre="${i}"]`),
      };
    }
    this.combos = Array.from(this.querySelectorAll('[data-nh-bb-combo]'));
    this.slots = Array.from(this.querySelectorAll('[data-nh-bb-slot]'));
    // The shared picker's rows. It is rendered in the page header, outside this
    // element, so it is found on the document — and it is `source: host`, which
    // means it waits for the destinations this section writes.
    this.rungs = Array.from(document.querySelectorAll('[data-nh-bp-row]'));
    // The closing band's link back to Mix & Match (round 11,
    // sections/nordenheim-bundle-outro.liquid). Its own section, so it is found
    // on the document exactly as the picker's rows are, and it takes the same
    // ?cap=&plate= handoff — the page's only route to the plate-by-plate flow now
    // that the dropdown carries bundles only.
    this.mixLinks = Array.from(document.querySelectorAll('[data-nh-bb-mixlink]'));
    this.stacked = this.tabs.length === 0;

    // Round 7: NOTHING is selected on a cold open — pickInitialCap returns null
    // unless the URL asked for a colourway. The stage still shows a plate on a
    // cap, because looking is not buying: `viewCap` falls back to the template's
    // default so the picture is never empty while the purchase is undecided.
    this.capKey = this.pickInitialCap();
    this.viewCap = this.capKey || this.defaultViewCap();

    this.bind();
    this.ready = true;

    // The clip is a function of the stage's live aspect ratio, and that ratio
    // moves with the window: the box is full-width with a viewport-capped
    // height. Without this the plate animation lands correctly at the width the
    // page loaded at and drifts at every other one.
    if ('ResizeObserver' in window && this.els.stage) {
      this.stageRo = new ResizeObserver(() => this.paintGeometry());
      this.stageRo.observe(this.els.stage);
    }

    this.bindCatRail();
    this.bindCompactStage();
    // The tile width is a function of the bar's measured width, so it is
    // re-solved whenever that changes - a rotation, a resize, or the bar first
    // appearing. The BAR is observed rather than `resize` being listened for:
    // it is the box the arithmetic actually reads.
    if ('ResizeObserver' in window && this.els.bar) {
      this.barRo = new ResizeObserver(() => {
        this.sizeBarTiles();
        // ROUND 15: the bar is the sheet's footer, so its height is also the
        // sheet's floor. A line appearing under the button (the pre-order note)
        // moves the floor, and the parked offset has to follow.
        if (this.sheetActive()) this.syncDockGeometry();
      });
      this.barRo.observe(this.els.bar);
    }

    this.paintTrayTitle();
    if (this.capKey) this.selectCap(this.capKey);
    else this.clearCap();
    this.applyCap(this.viewCap, { animate: false });
    // A plate on the stage from the first paint: an empty stage with an empty
    // gallery beside it reads as broken rather than as an invitation.
    //
    // ROUND 14: on a PHONE that seed must not also become a candidate. The
    // candidate tile sits in the buy bar at the same size and in the same place
    // as a chosen plate, differing only by opacity and a dashed outline — so a
    // cold page opened as though one of the three were already picked. `seed`
    // marks the preview as untouched; the first real tap clears it. The stage
    // still shows a plate, which is the half of this that was worth keeping.
    this.preview(this.firstPlate(), { animate: false, seed: true });
    this.restoreFromUrl();
    this.renderTray();
    this.syncBuy();
    this.moveBuy();
    this.markChosen();
  }

  /* ------------------------------------------------------- the tray sheet */

  /**
   * Is the Tray Sheet the phone layout for this instance? A section setting
   * (`phone_mode`), read off the element, plus the dock actually being in the
   * markup — `band` renders no dock at all, which is the whole rollback.
   */
  sheetMode() {
    return !!(this.els && this.els.dock && this.els.sheet) && this.dataset.phoneMode !== 'band';
  }

  /** Sheet mode AND below 901px. Every new code path is gated on this, so the
   *  desktop layout is byte-for-byte what it was. */
  sheetActive() {
    return this.sheetMode() && this.compactWidth();
  }

  /**
   * True below 901px — the phone layout.
   *
   * `this.mq` is created when the events are bound, which is AFTER build(), so
   * this falls back to a fresh query rather than reading undefined. One place
   * asks the question, so the breakpoint is written once.
   */
  compactWidth() {
    if (this.mq) return !!this.mq.matches;
    return window.matchMedia('(max-width: 900px)').matches;
  }

  /** The first plate anywhere in the wall that has an image for this cap. */
  firstPlate() {
    const cats = this.data.categories || [];
    for (let ci = 0; ci < cats.length; ci += 1) {
      const plates = (cats[ci] && cats[ci].plates) || [];
      for (let pi = 0; pi < plates.length; pi += 1) {
        if (stageSrc(plates[pi], this.viewCap)) return { ci, pi };
      }
    }
    return null;
  }

  /**
   * Restore a colourway and a set of plates from the query string.
   *
   * READ ONLY — nothing here ever writes to the address bar. The parameters
   * exist so the size switcher can carry your state from one bundle page to the
   * next; a live sync would fight the browser's history on every click and make
   * any copied link a lottery.
   *
   * Everything is restored by CLICKING the real controls rather than by setting
   * state directly. That is the lesson from plate-snap: calling the internals
   * skips the aria, the roving tabindex, the tray and the hidden inputs, and
   * they drift apart in ways that only show up later.
   */
  restoreFromUrl() {
    let params;
    try {
      params = new URL(window.location.href).searchParams;
    } catch {
      return;
    }

    const capWanted = (params.get('cap') || '').trim().toLowerCase();
    if (capWanted && this.cap(capWanted) && this.cap(capWanted).key === capWanted) {
      this.selectCap(capWanted);
      this.applyCap(capWanted, { animate: false });
    }

    const raw = (params.get('plates') || params.get('plate') || '').trim();
    if (!raw) return;
    const handles = raw.split(',').map((h) => h.trim()).filter(Boolean);
    const cats = this.data.categories || [];
    handles.forEach((handle) => {
      if (this.chosen.length >= this.max) return;
      for (let ci = 0; ci < cats.length; ci += 1) {
        const plates = (cats[ci] && cats[ci].plates) || [];
        const pi = plates.findIndex((pl) => pl && pl.handle === handle);
        if (pi < 0) continue;
        // Straight into the model: there is no per-tile control to click any
        // more, and the candidate flow is for a human deciding one at a time.
        this.addPlate({ ci, pi });
        return;
      }
    });
  }

  readData() {
    const node = this.querySelector('script[type="application/json"][data-nh-bb-data]');
    if (!node) return null;
    try {
      const parsed = JSON.parse(node.textContent);
      if (!parsed || !Array.isArray(parsed.caps) || !Array.isArray(parsed.categories)) return null;
      if (!parsed.caps.length || !parsed.categories.length) return null;
      return parsed;
    } catch {
      return null;
    }
  }

  /**
   * The colourway being BOUGHT at load — null unless the URL asked for one.
   *
   * ?cap= is the Mix & Match handoff and carries a real decision, so it is
   * honoured. ?variant= is Shopify's own deep link and does the same, through
   * the server-rendered data-default-cap. Anything else is a cold open, and a
   * cold open must not pre-buy a colourway the shopper never chose — that was
   * the whole of Sina's round 7 note.
   */
  pickInitialCap() {
    let params = null;
    try {
      params = new URL(window.location.href).searchParams;
    } catch {
      params = null;
    }
    if (params) {
      const wantedCap = (params.get('cap') || '').trim().toLowerCase();
      const byCap = this.data.caps.find((c) => c.key === wantedCap);
      if (byCap) return byCap.key;
      if (params.has('variant')) {
        const attr = this.getAttribute('data-default-cap');
        const byVariant = this.data.caps.find((c) => c.key === attr);
        if (byVariant) return byVariant.key;
      }
    }
    return null;
  }

  /** The colourway the STAGE opens on. Never null — the picture needs a cap. */
  defaultViewCap() {
    const attr = this.getAttribute('data-default-cap');
    const found = this.data.caps.find((c) => c.key === attr);
    return (found || this.data.caps[0]).key;
  }

  /** The cap being bought, or null while none is picked. */
  capChosen() {
    if (!this.capKey) return null;
    return this.data.caps.find((c) => c.key === this.capKey) || null;
  }

  /** Back to "Pick your cap": no row selected, the drawing on the button. */
  /* ROUND 19 — capAffix() and syncCapTileLabel() moved to
     assets/nordenheim-bundle-core.js: the cap control is shared with Mix &
     Match now, and its button's word and its phone-tile name are part of it. */


  clearCap() {
    this.capKey = null;
    const capList = this.els.capList || this.els.capCombo;
    if (capList) {
      capList.querySelectorAll('[data-nh-bb-capopt]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
    }
    const thumb = this.els.capBtnThumb;
    if (thumb) {
      const drawing = thumb.dataset.nhBbCapDrawing || '';
      thumb.classList.add('nh-bb__combo-thumb--placeholder');
      if (drawing) thumb.style.setProperty('--nh-bb-combo-img', `url("${cssUrl(drawing)}")`);
    }
    if (this.els.capName) {
      this.els.capName.textContent = this.labels.capPlaceholder || 'Pick your cap';
    }
    this.capAffix(false);
    this.paintSubline(null);
    this.syncCapRoving();
    this.syncBuy();
    this.updateRungLinks();
    this.paintCapNote();
  }

  cap(key) {
    return this.data.caps.find((c) => c.key === key) || this.data.caps[0];
  }

  plateAt(ci, pi) {
    const cat = this.data.categories[ci];
    return (cat && cat.plates && cat.plates[pi]) || null;
  }

  on(target, type, handler, opts) {
    target.addEventListener(type, handler, opts);
    this.listeners.push([target, type, handler, opts]);
  }

  /* ----------------------------------------------------------------- bind */

  bind() {
    // One delegated change handler for both radio groups and the checkbox wall:
    // 200 tiles means 200 listeners otherwise, and every one of them would have
    // to be re-bound if the section ever re-rendered.
    // ROUND 15: NAMED, because the Tray Sheet moves the bar (the tray, its
    // checkboxes and the cap tile) into the dock — a child of the SECTION and
    // a sibling of this element, so nothing in it bubbles here. bindSheet()
    // attaches the same two handlers to the dock; an event reaches exactly one
    // of the two roots, so nothing is handled twice.
    this.onDelegatedChange = (e) => {
      const confirm = e.target.closest('[data-nh-bb-confirm]');
      if (confirm) {
        this.onSlotToggle(confirm);
        return;
      }
    };
    this.on(this, 'change', this.onDelegatedChange);

    // Previewing. Delegated like everything else — 200 tiles means 200
    // listeners otherwise, all of them to be re-bound on a section re-render.
    this.onDelegatedClick = (e) => {
      const tile = e.target.closest && e.target.closest('[data-nh-bb-preview]');
      if (tile) {
        // ROUND 15: on a phone the tap opens the Tray Sheet. The sheet is
        // opened BEFORE preview() starts the snap — the stage already lives in
        // the sheet, so nothing is reparented mid-animation, but the order is
        // the contract: reparenting restarts a CSS animation.
        // ROUND 15.1: a tap inside the quarter-second "Added ✓" window is a
        // new preview, not something to park on top of — the pending park is
        // cancelled and the button reads the new plate's state.
        this.settleAdded();
        if (this.sheetActive()) this.setSheet(true);
        this.preview({ ci: Number(tile.dataset.cat), pi: Number(tile.dataset.index) },
                     { animate: true });
        return;
      }
      const slot = e.target.closest && e.target.closest('[data-nh-bb-slot-preview]');
      if (slot) {
        const li = slot.closest('.nh-bb__slot');
        if (li && li.classList.contains('is-plus')) {
          this.revealWall();
          return;
        }
        const choice = this.chosen[Number(slot.dataset.nhBbSlotPreview)];
        if (choice) {
          // A chosen plate tapped in the fan reopens the sheet on it, with the
          // button reading "In your bundle — remove".
          this.settleAdded();
          if (this.sheetActive()) this.setSheet(true);
          this.preview(choice, { animate: true });
        }
        return;
      }
      const capChip = e.target.closest && e.target.closest('[data-nh-bb-chip-cap]');
      if (capChip) {
        // The strip changes what you SEE, on every viewport. The dropdown (or
        // the phone's centred chooser) is what decides the variant, and it is
        // deliberately not touched here (Sina, 2026-09-02; re-stated 2026-09-03,
        // round 17 item 5).
        //
        // ROUND 17 — DECOUPLED AGAIN. Round 15.1 had the sheet's chips also
        // call selectCap(), because the sheet then carried the 2x4 cap grid one
        // row above them and the two controls would otherwise disagree. That
        // grid is gone (round 16), so the coupling was left doing the one thing
        // it was never meant to: a look at Björk silently changed the cap in
        // the cart. Now a chip moves only `viewCap` (stage + caption); the bar's
        // cap tile keeps showing the PURCHASED cap, so the two never look the
        // same when they differ. The cap chooser is the only route to `capKey`.
        const chipKey = capChip.dataset.nhBbChipCap;
        this.applyCap(chipKey, { animate: true });
        return;
      }
      const chip = e.target.closest && e.target.closest('[data-nh-bb-shot]');
      if (chip) {
        this.openShot(Number(chip.dataset.nhBbShot));
        return;
      }
    };
    this.on(this, 'click', this.onDelegatedClick);

    // Roving tabindex over the wall. A checkbox group is not a radio group, so
    // the browser gives every tile its own tab stop — 200 of them. Arrow keys
    // move focus instead and only the active tile is tabbable, which is the
    // standard grid pattern; Space and Enter still toggle natively.
    this.on(this, 'keydown', (e) => {
      const tile = e.target.closest && e.target.closest('[data-nh-bb-preview]');
      if (tile) {
        this.onWallKeydown(e, tile);
        return;
      }
      const tab = e.target.closest && e.target.closest('[data-nh-bb-tab]');
      if (tab) this.onTabKeydown(e, this.tabs.indexOf(tab));
    });
    this.on(this, 'focusin', (e) => {
      const tile = e.target.closest && e.target.closest('[data-nh-bb-preview]');
      if (tile) this.setRoving(tile);
    });

    this.tabs.forEach((tab, i) => {
      this.on(tab, 'click', () => this.selectCategory(i, { focusTab: false, scroll: true }));
    });

    this.bindCombos();
    this.bindInfo();

    // Preload the composite the shopper is about to ask for.
    const intent = (e) => {
      const cell = e.target.closest && e.target.closest('.nh-bb__cell');
      if (!cell) return;
      const tile = cell.querySelector('[data-nh-bb-preview]');
      if (!tile) return;
      const plate = this.plateAt(Number(tile.dataset.cat), Number(tile.dataset.index));
      if (plate) this.preload(stageSrc(plate, this.viewCap));
    };
    this.on(this, 'pointerover', intent, { passive: true });
    this.on(this, 'focusin', intent);

    if (this.els.button) {
      this.on(this.els.button, 'click', (e) => this.onAddToCart(e));
      // Broadcast fires these on the button once its own fetch resolves.
      this.on(this.els.button, 'theme:product:add', () => this.announceAdd(true));
      this.on(this.els.button, 'theme:product:add-error', (e) => this.announceAdd(false, e));
    }

    // Below 901px the buy block leaves the panel and lives in the sticky bar.
    // Moved, never cloned: one form, one button, one set of hidden inputs.
    this.mq = window.matchMedia('(max-width: 900px)');
    // ROUND 14: renderTray as well as moveBuy. `candidate()` now answers
    // differently on either side of the breakpoint, so widening a phone window
    // to desktop has to redraw the tray or the candidate stays suppressed.
    // ROUND 14: paintStage too. Which treatment a worn shot gets depends on the
    // breakpoint — the box grows on desktop and the band crops on a phone — so
    // crossing it while a shot is open has to re-decide.
    this.mqHandler = () => { this.moveBuy(); this.renderTray(); this.paintStage(); };
    if (this.mq.addEventListener) this.mq.addEventListener('change', this.mqHandler);
    else if (this.mq.addListener) this.mq.addListener(this.mqHandler);
  }

  /* ROUND 19 — the COMBOBOX moved to assets/nordenheim-bundle-core.js.
     bindCombos / comboOpen / comboList / toggleCombo / openCapModal /
     closeCapModal / onComboKeydown / chooseOption are one implementation now:
     Mix & Match's "Your cap" control is this control, not a second one that
     looks like it. Moved unchanged; see the core for the bodies. */

  /* ROUND 19 — the INFO POPOVER moved to assets/nordenheim-bundle-core.js
     (bindInfo / toggleInfo / syncInfo / fillInfo / paintInfoBody / loadInfo /
     placeInfo), with the caption row it hangs off. Moved unchanged. */

  onTabKeydown(e, i) {
    if (i < 0) return;
    const live = this.tabs.map((t, idx) => (t.hidden ? -1 : idx)).filter((idx) => idx >= 0);
    if (live.length < 2) return;
    const at = live.indexOf(i);
    const last = live.length - 1;
    let pos = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') pos = at === last ? 0 : at + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') pos = at === 0 ? last : at - 1;
    else if (e.key === 'Home') pos = 0;
    else if (e.key === 'End') pos = last;
    if (pos === null) return;
    e.preventDefault();
    this.selectCategory(live[pos], { focusTab: true, scroll: true });
  }

  /**
   * Arrow keys across the wall.
   *
   * The column count is read from the rendered grid rather than assumed, so
   * Up/Down move a real row whatever the breakpoint has resolved to.
   */
  onWallKeydown(e, input) {
    const keys = ['ArrowRight', 'ArrowLeft', 'ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (keys.indexOf(e.key) < 0) return;
    const grid = input.closest('.nh-bb__tiles');
    if (!grid) return;
    const items = Array.from(grid.querySelectorAll('[data-nh-bb-preview]')).filter(
      (el) => !el.closest('.nh-bb__cell').hidden
    );
    const at = items.indexOf(input);
    if (at < 0) return;
    let cols = 1;
    try {
      cols = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length || 1;
    } catch {
      cols = 1;
    }
    let next = at;
    if (e.key === 'ArrowRight') next = at + 1;
    else if (e.key === 'ArrowLeft') next = at - 1;
    else if (e.key === 'ArrowDown') next = at + cols;
    else if (e.key === 'ArrowUp') next = at - cols;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = items.length - 1;
    if (next < 0 || next >= items.length) return;
    e.preventDefault();
    this.setRoving(items[next]);
    items[next].focus();
  }

  /**
   * Roving tabindex across the wall.
   *
   * A hundred tiles would otherwise be a hundred tab stops. Only the active tile
   * is tabbable and the arrow keys move it — the standard grid pattern. There is
   * exactly one control per tile again now that the Add toggles have gone, so
   * this is back to being a single list.
   */
  setRoving(tile) {
    this.previewButtons.forEach((el) => { el.tabIndex = el === tile ? 0 : -1; });
  }

  /**
   * THE CATEGORY ROW (round 11, Sina): sticky on every width, a horizontal rail
   * below 901px.
   *
   * Two jobs, both of them observation rather than layout — the layout is all in
   * assets/nordenheim-chips.css:
   *
   *   1. `is-stuck` on the wrapper once it is actually pinned, so the hairline
   *      and its shadow only appear when there is content sliding underneath.
   *      A 1px SENTINEL above the row is what is watched: `position: sticky` has
   *      no state selector, and an IntersectionObserver costs nothing between
   *      transitions where a scroll listener runs on every frame.
   *
   *      rootMargin's top is the sticky offset itself, read from the same custom
   *      property the CSS uses — otherwise the class would flip a header's height
   *      too early on every page where the header is sticky.
   *
   *   2. `has-start` / `has-end` for the edge fades, from the scroller's own
   *      position. A rail whose content fits shows neither, which is what stops
   *      the fade appearing over nothing on a wide phone.
   */
  bindCatRail() {
    const wrap = this.els.catwrap;
    const rail = this.els.cats;
    if (!wrap || !rail) return;

    const sentinel = this.els.catsentinel;
    if (sentinel && 'IntersectionObserver' in window) {
      // The offset the CSS sticks at, in pixels, so the observer flips at the
      // same line the element actually stops on.
      const top = () => {
        const cs = getComputedStyle(wrap);
        const n = parseFloat(cs.top);
        return Number.isFinite(n) ? n : 0;
      };
      this.stickRo = new IntersectionObserver((entries) => {
        entries.forEach((e) => wrap.classList.toggle('is-stuck', !e.isIntersecting));
      }, { rootMargin: `-${Math.round(top()) + 1}px 0px 0px 0px`, threshold: 0 });
      this.stickRo.observe(sentinel);
    }

    const fades = () => {
      const over = rail.scrollWidth - rail.clientWidth;
      // 2px of slack: sub-pixel layout leaves scrollLeft a fraction short of the
      // end and the right-hand fade would never switch off.
      wrap.classList.toggle('has-start', rail.scrollLeft > 2);
      wrap.classList.toggle('has-end', over > 2 && rail.scrollLeft < over - 2);
    };
    this.on(rail, 'scroll', fades, { passive: true });
    this.railFades = fades;
    if ('ResizeObserver' in window) {
      this.railRo = new ResizeObserver(fades);
      this.railRo.observe(rail);
    }
    fades();
  }

  /**
   * THE COMPACT STICKY PICTURE, below 901px (round 11, Sina).
   *
   * The cap used to scroll away and leave you browsing a hundred plates with
   * nothing to see them on. Once the viewing zone leaves the top of the screen,
   * the REAL stage and the REAL caption move into a fixed box under the header.
   *
   * Three things make this a MOVE rather than a shrink or a copy:
   *
   *   - One stage. The snap animation appends its incoming layer to
   *     `this.els.stage`, so a plate tapped while the compact header is up snaps
   *     onto the cap exactly as it does at the top of the page. A cloned picture
   *     would have been a second thing to keep in step, and it would not snap.
   *   - No layout shift. The viewing zone is pinned to the height it measured
   *     before the move, so the document does not get shorter and nothing under
   *     the finger moves. A sticky element that shrinks when it sticks pulls
   *     everything below it up by the difference; there is no honest way around
   *     that except not to change the flow at all.
   *   - The crop is not re-invented. The box gets shorter, and round 10's
   *     identity does the rest: the box's height is capped, so the PICTURE
   *     narrows to keep the same 11%..91% band of the composite and the bleed
   *     layer fills the sides with the composite's own ground. `paintGeometry`
   *     re-derives the snap clip from the MEASURED box (a ResizeObserver on the
   *     stage), so the plate face still lands inside it without a second set of
   *     numbers to keep true.
   */
  bindCompactStage() {
    const head = this.els.stickyHead;
    const start = this.els.viewSentinel;
    const end = this.els.endSentinel;
    if (!head || !start || !('IntersectionObserver' in window)) return;

    // TWO SENTINELS, ONE STATE. The header is up only while the viewing zone has
    // gone above the line AND the builder has not yet finished passing it.
    // `position: fixed` releases nothing by itself, so without the second one
    // the cap stayed pinned over `meetplate` and everything below it — measured
    // at scrollY 1200, still at top 0 with the next section scrolling under it.
    this.pastStart = false;
    this.beforeEnd = true;
    const apply = () => this.setCompactStage(this.pastStart && this.beforeEnd);

    // The line both are measured against: the site header plus the compact
    // band's own height, so the release lands as the builder's last pixel
    // reaches the BOTTOM of the pinned band rather than the top of the screen.
    // The height is read from the real box the first time the band is up, and
    // the observer is rebuilt if it differs from the estimate.
    const build = () => {
      if (this.compactIo) this.compactIo.disconnect();
      const line = this.headerOffset() + (this.compactH || 181);
      this.compactMargin = Math.round(line);
      // ABOVE THE LINE IS `top < margin`, NOT `top < 0`. The root is the viewport
      // shrunk from the top by `compactMargin`, so an element that has passed it
      // still has a positive `top` — 123px, measured, at the very scroll position
      // where the header should have come on. The old test compared against 0,
      // which is only correct while the margin is 0 (it was, until the release
      // line was introduced), and it is why this looked like an observer that
      // would not fire: it fired every time and the condition threw the answer
      // away. Checked against a hand-built observer on the same two targets.
      const edge = this.compactMargin;
      this.compactIo = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.target === start) {
            this.pastStart = !e.isIntersecting && e.boundingClientRect.top < edge;
          } else {
            this.beforeEnd = e.isIntersecting || e.boundingClientRect.top > edge;
          }
        });
        apply();
      }, { rootMargin: `-${this.compactMargin}px 0px 0px 0px`, threshold: 0 });
      this.compactIo.observe(start);
      if (end) this.compactIo.observe(end);
    };
    this.rebuildCompactIo = build;
    build();
  }

  headerOffset() {
    const n = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--header-sticky-height'));
    return Number.isFinite(n) ? Math.round(n) : 0;
  }

  setCompactStage(on) {
    const head = this.els.stickyHead;
    const view = this.els.view;
    const stage = this.els.stage;
    const cap = this.els.capline;
    if (!head || !view || !stage || !cap) return;
    // ROUND 15: the pinned band and the Tray Sheet never coexist. While the
    // sheet is the phone layout the band can only ever be switched OFF here.
    const want = !!on && !!(this.mq && this.mq.matches) && !this.sheetActive();
    if (want === !!this.compact) return;
    this.compact = want;
    if (want) {
      // Freeze the zone at what it measures NOW, before anything leaves it.
      view.style.minHeight = `${Math.round(view.getBoundingClientRect().height)}px`;
      head.appendChild(stage);
      head.appendChild(cap);
      head.hidden = false;
      this.classList.add('nh-bb--compact');
    } else {
      this.classList.remove('nh-bb--compact');
      head.hidden = true;
      // Back in DOM order: the stage first, then the caption above the bands.
      view.insertBefore(stage, view.firstChild);
      const bands = view.querySelector('.nh-bb__bands');
      if (bands) bands.insertBefore(cap, bands.firstChild);
      view.style.minHeight = '';
    }
    // The chip row sticks BELOW this box, so it has to know how tall it is —
    // and it changes with the caption's own height, so it is measured rather
    // than written down.
    const headH = Math.round(head.getBoundingClientRect().height);
    (this.closest('.nh-bb') || this).style.setProperty('--nh-bb-stuckh',
      want ? `${headH}px` : '0px');
    // The release line is the site header plus this band. Measured here, the
    // first time the band actually exists, and the observer rebuilt if the
    // estimate it was built with was wrong.
    if (want && headH > 0 && Math.abs((this.compactH || 0) - headH) > 1) {
      this.compactH = headH;
      if (this.rebuildCompactIo
          && Math.abs(this.compactMargin - (this.headerOffset() + headH)) > 1) {
        this.rebuildCompactIo();
      }
    }
    // The box changed shape; the clip is a function of that shape.
    // ROUND 14: which treatment a worn shot gets depends on WHICH box it is in
    // — the full-width stage grows to the photograph's own shape, the pinned
    // 150px band crops it. Moving between the two has to re-decide, and this is
    // the one function every move goes through.
    this.paintStage();
    this.paintGeometry();
  }


  /** Bring a chip into view inside the phone rail. No-op where it already is. */
  revealTab(tab) {
    const rail = this.els.cats;
    if (!tab || !rail || rail.scrollWidth <= rail.clientWidth) return;
    const r = tab.getBoundingClientRect();
    const box = rail.getBoundingClientRect();
    if (r.left >= box.left && r.right <= box.right) return;
    tab.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    if (this.railFades) this.railFades();
  }

  /**
   * After a USER switches category on a phone, put the new category's first row
   * where the reader is looking (round 11, Sina).
   *
   * The chips are sticky, so switching from halfway down a long category left the
   * new one's first row above the viewport — the tap appeared to do nothing but
   * change the pills. The landing point is measured, not guessed: the header's
   * own sticky height plus the chip row's actual box, both read at the moment of
   * the tap, because the chip row's height changes with the wrap.
   *
   * Phone only. On desktop the wall sits beside a sticky panel with the chips
   * already in view, and a page that jumps under a mouse click is worse than one
   * that does not.
   *
   * USER-INITIATED ONLY. The initial build, a ?cat= style restore and every
   * programmatic call pass nothing, so the page cannot yank itself around on
   * load — which is the failure this would otherwise introduce.
   */
  revealCategory(index) {
    if (!this.mq || !this.mq.matches) return;
    const panel = this.catPanels[index];
    if (!panel) return;

    // Where the chip row COMES TO REST, not where it happens to be right now.
    // Its computed `top` already contains the site header AND the compact
    // picture header (--nh-bb-stuckh, which the JS measures as that box
    // activates), so reading it is how this stays right without adding up three
    // numbers that each move on their own. Measured the other way first, and the
    // first tile landed 181px up behind the compact header — exactly its height.
    const wrap = this.els.catwrap;
    let stickBottom = 0;
    if (wrap) {
      const cs = getComputedStyle(wrap);
      stickBottom = (parseFloat(cs.top) || 0) + wrap.getBoundingClientRect().height;
    }
    const y = window.scrollY + panel.getBoundingClientRect().top - stickBottom - 8;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: Math.max(0, y), behavior: still ? 'auto' : 'smooth' });
  }

  selectCategory(index, { focusTab, scroll = false }) {
    this.tabs.forEach((tab, i) => {
      const on = i === index;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      if (on && focusTab) tab.focus();
      // Below 901px the row is a scroller, so the selected category can be off
      // screen — on load (the URL may have restored one) and after an arrow key
      // moved the roving tabindex to a chip past the edge. `nearest` on both
      // axes: `inline: 'center'` would yank the rail on every click, and a
      // `block` scroll would move the PAGE under a sticky row.
      if (on) this.revealTab(tab);
    });
    if (!this.stacked) {
      this.catPanels.forEach((panel, i) => {
        panel.hidden = i !== index;
      });
    }
    // Focus must not be left on a tile inside a panel that just went away.
    const first = this.catPanels[index] && this.catPanels[index].querySelector('[data-nh-bb-preview]');
    if (first) this.setRoving(first);
    if (scroll) this.revealCategory(index);
  }

  /* ------------------------------------------------------------ selection */


  /**
   * Put a plate on the cap, and offer it in the tray's next empty slot.
   *
   * Previewing and adding were one action until 2026-09-02, which meant browsing
   * the wall spent the bundle: three taps to look at three plates and the tray
   * was full. Now a click only LOOKS, and the candidate slot is where the offer
   * to buy it lives — next to the other plates you have chosen, which is the one
   * place the decision actually makes sense.
   */
  preview(ref, { animate, seed = false } = {}) {
    if (!ref) return;
    const plate = this.plateAt(ref.ci, ref.pi);
    if (!plate) return;
    // ROUND 14. Only the build's own seeding passes `seed`; every other caller
    // is a shopper action, so the flag clears itself on the first real tap
    // without any caller having to remember to.
    this.untouched = seed;
    this.previewed = { ci: ref.ci, pi: ref.pi };
    // A new plate invalidates the open photo — the shots belong to the old one.
    this.shotIndex = -1;
    this.markPreviewed();
    this.renderStrip();
    this.paintStage({ snap: animate !== false });
    // The caption names the plate, so it has to be rewritten whenever the plate
    // changes — not only when the colourway or the open photo does. Without this
    // it stayed blank from the first paint, because the only other callers run
    // before anything is previewed.
    this.paintCapNote();
    this.renderTray();
    this.announceCandidate();
  }

















  /* ----------------------------------------------------------------- tray */



  /**
   * What the "+" does: take the shopper to the plates.
   *
   * Scrolls the wall's current category under the sticky chrome — reusing
   * revealCategory, so the landing point is the one the chip row already
   * defines — and moves focus to the tile the roving tabindex is on, which is
   * where an arrow key would carry on from.
   */
  revealWall() {
    const index = this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true');
    // The compact picture header is turned on BEFORE the landing point is
    // computed, not after. revealCategory reads the chip row's resting `top`,
    // which contains --nh-bb-stuckh — and pressing the "+" from the top of the
    // page means that header is still off, so the row's top was 181px short and
    // the first tile landed behind it. Measured: tile at 69.5 against a chip row
    // ending at 243.
    //
    // Turning it on early costs nothing: the move keeps the viewing zone's
    // measured height, so the document does not change size, and the destination
    // is always past the sentinel that would have switched it on a moment later.
    if (!this.compact) this.setCompactStage(true);
    this.revealCategory(index < 0 ? 0 : index);
    const panel = this.catPanels[index < 0 ? 0 : index];
    const tile = panel && (panel.querySelector('[data-nh-bb-preview][tabindex="0"]')
      || panel.querySelector('[data-nh-bb-preview]'));
    // preventScroll: revealCategory has already decided where the page goes, and
    // focus() would otherwise scroll the tile to a different place.
    if (tile) tile.focus({ preventScroll: true });
  }


  /* ------------------------------------------------------------------ buy */






  /* ------------------------------------------------------------ colourway */

  /**
   * Choose the colourway that is BOUGHT.
   *
   * Deliberately does not touch the stage (Sina, 2026-09-02). This row and the
   * gallery strip answer different questions — "which cap am I buying" and
   * "which cap am I looking at" — and tying them together meant you could not
   * compare a plate across colourways without changing your order.
   */
  selectCap(key) {
    const cap = this.cap(key);
    if (!cap) return;
    this.capKey = cap.key;

    // ROUND 19 — the painting is the core's paintCapButton(), which Mix & Match
    // calls too. Same rows, same thumbnail, same "Your cap – <name>" line; what
    // stays here is the STATE, which is this surface's alone.
    this.paintCapButton(cap);
    this.paintSubline(cap);
    this.syncCapRoving();
    this.syncBuy();
    this.updateRungLinks();
    // The caption names the DISPLAYED cap, but the plate link in it can change
    // when the purchase does, so it is rewritten either way.
    this.paintCapNote();
  }

  /**
   * Where the picture is inside the stage, measured rather than assumed.
   *
   * The box's height is capped against the viewport, and once that cap bites
   * the picture narrows rather than the crop deepening (round 10) — so both
   * numbers move with the window and the clip has to follow. Measured on every
   * paint and re-run from a ResizeObserver for that reason.
   *
   * The picture's width is min(the box, the height the crop needs), which is
   * the same min() the stylesheet writes into --nh-bb-pic-w. It is recomputed
   * here from the MEASURED box rather than read back out of the property so
   * that neither side has to re-derive the other's formula; they agree because
   * the box's height is min(width / fill, ceiling) at every width.
   */
  stageGeom() {
    const none = { vRatio: 1, picFrac: 1 };
    const st = this.els && this.els.stage;
    if (!st) return none;
    const r = st.getBoundingClientRect();
    if (!r.width || !r.height) return none;
    const pic = Math.min(r.width, r.height * this.stageFill());
    if (!(pic > 0)) return none;
    // Never below 1: a picture shorter than the box would crop horizontally
    // instead, which is a different sum and a shape this section never renders.
    return { vRatio: Math.max(1, pic / r.height), picFrac: Math.min(1, pic / r.width) };
  }

  /**
   * The square's side as a multiple of the box's height — the crop's depth,
   * read from the stylesheet so the two can only move together.
   */
  stageFill() {
    const el = (this.els && this.els.stage) || this;
    const n = parseFloat(getComputedStyle(el).getPropertyValue('--nh-bb-stage-fill'));
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  /**
   * Where the landscape crop is anchored, read from the stylesheet so the CSS
   * and the clip arithmetic can never disagree about it.
   */
  stageFocus() {
    const el = (this.els && this.els.stage) || this;
    const raw = getComputedStyle(el).getPropertyValue('--nh-bb-stage-focus');
    const n = parseFloat(raw);
    if (!Number.isFinite(n)) return 0.5;
    return clamp(n / 100, 0, 1);
  }

  /* ROUND 21 — paintGround() moved to assets/nordenheim-bundle-core.js.
     Mix & Match's card takes the same per-colourway ground now, so the one
     implementation is shared exactly as round 19 shared the caption row, the
     strip and the cap control. This class still calls it, unchanged, and the
     bundle PDPs are byte-for-byte what they were. Its stylesheet moved with it,
     into assets/nordenheim-bundle-tray.css. */

  /**
   * THE COMPACT BAND'S CROP (round 11, second pass).
   *
   * The band used to be the picture at its own width — 187.5px centred in a 375
   * screen with ground either side, which read as a floating thumbnail rather
   * than a pinned header. It now spans the full width and crops harder instead:
   * a wide, short band.
   *
   * Both constants are written onto the STAGE rather than hardcoded, because the
   * shape is a function of the measured box:
   *
   *   fill  = boxW / boxH. That is what makes --nh-bb-pic-w resolve to the full
   *           width (min(100%, boxH * fill) === boxW), so the picture covers the
   *           band with no surround, and it is what stageGeom() reads back.
   *   focus = whatever puts the PLATE in the middle of the band. With `cover` the
   *           visible band is [f(1-F), f(1-F)+F] where F = boxH/boxW, so
   *           f = (plateCentre - F/2) / (1 - F).
   *
   * The whole cap cannot be in a band this shallow — a square photograph shown
   * full width at 150px tall exposes 40% of itself, and the cap needs 77%. So the
   * band is centred on the plate, which is the part that changes and the part the
   * shopper is choosing. Solved per colourway from that cap's own `plate_rect`,
   * so it is right for all eight rather than tuned on one.
   *
   * Cleared in the full-size state, where round 10's constants take over again.
   */
  syncCompactCrop() {
    const st = this.els && this.els.stage;
    if (!st) return;
    if (!this.compact) {
      st.style.removeProperty('--nh-bb-stage-fill');
      st.style.removeProperty('--nh-bb-stage-focus');
      return;
    }
    const r = st.getBoundingClientRect();
    if (!(r.width > 0 && r.height > 0)) return;
    const fill = r.width / r.height;
    const F = r.height / r.width;
    const rect = (this.cap(this.viewCap) || {}).plate || {};
    const top = Number(rect.top);
    const height = Number(rect.height);
    const centre = Number.isFinite(top) && Number.isFinite(height)
      ? (top + height / 2) / 100
      : 0.385;
    const f = F < 1 ? clamp((centre - F / 2) / (1 - F), 0, 1) : 0.5;
    st.style.setProperty('--nh-bb-stage-fill', fill.toFixed(4));
    st.style.setProperty('--nh-bb-stage-focus', `${(f * 100).toFixed(2)}%`);
  }

  /** Re-derive the snap clip and the tile crop for the displayed colourway. */
  paintGeometry() {
    // The compact band's shape is a function of its measured box, and the clip
    // below is a function of that shape — so it is re-solved first, on every
    // path that re-derives the clip (a colourway change, a resize, the move into
    // and out of the band).
    this.syncCompactCrop();
    if (!this.data || !this.viewCap) return;
    const cap = this.cap(this.viewCap);
    if (!cap) return;
    const g = this.stageGeom();
    const geo = derive(cap.plate || {}, this.settings.thumbAspect,
                       this.settings.clipPadPercent, g.vRatio, this.stageFocus(), g.picFrac);
    const s = this.style;
    s.setProperty('--nh-bb-clip', geo.clip);
    // ROUND 15.1: ON THE STAGE TOO. In the Tray Sheet the stage lives in the
    // dock — a sibling of this element — so a custom property written here
    // never reaches it and the incoming layer fell back to the stylesheet's
    // default clip (measured: inset(25% 30% 48% 29%) painted where
    // inset(15.75% 28.28% 46.56% 27.41%) was derived). On desktop the stage is
    // inside this element and the inline value equals the inherited one.
    if (this.els.stage) this.els.stage.style.setProperty('--nh-bb-clip', geo.clip);
    s.setProperty('--nh-bb-thumb-size', `${geo.thumb.size.toFixed(2)}%`);
    s.setProperty('--nh-bb-thumb-x', `${geo.thumb.x.toFixed(2)}%`);
    s.setProperty('--nh-bb-thumb-y', `${geo.thumb.y.toFixed(2)}%`);
  }

  /** Change the colourway on the STAGE. The purchase is untouched. */
  applyCap(key, { animate }) {
    const cap = this.cap(key);
    if (!cap) return;
    this.viewCap = cap.key;

    // Supersede any swap still waiting on its image to decode, or it would paint
    // the previous colourway once it resolves.
    this.swapSeq = (this.swapSeq || 0) + 1;
    if (this.pending) {
      this.pending.remove();
      this.pending = null;
      this.pendingSrc = null;
    }

    this.paintGeometry();

    // Deliberately does NOT tick the radio. It used to, from when the two were
    // one thing, and the result was that viewing a plate on another colourway
    // silently changed the variant in the cart — the row said "Björk" while the
    // ticked card said "Midnatt". selectCap owns that control; this owns the
    // picture.

    this.repaintTiles();
    // A displayed-colourway change returns the stage to the live combination: an
    // open worn photo was shot on one particular cap, so leaving it up while the
    // strip says another would be a picture contradicting its own caption.
    this.shotIndex = -1;
    this.renderStrip();
    this.paintStage({ fade: animate });
    this.paintGround({ fade: animate });
    this.paintCapNote();
  }

  /* ROUND 19 — the CAPTION ROW's painter moved to
     assets/nordenheim-bundle-core.js (paintCapNote / noteTimes / noteLink):
     "<Plate> × <Cap>", both names linked, the sentence kept for a screen
     reader. Moved unchanged. */

  /**
   * Reconcile the wall with the selected colourway.
   *
   * A tile you can click has to lead somewhere, so a plate with no stage mockup
   * for this cap is hidden — and hidden on the CELL, because the label carries a
   * `display` declaration and [hidden] is only type-strength, so it would lose.
   * A hidden tile that is already chosen stays chosen: the tray, not the wall,
   * is the record of what is being bought.
   */
  repaintTiles() {
    this.previewButtons.forEach((tile) => {
      const plate = this.plateAt(Number(tile.dataset.cat), Number(tile.dataset.index));
      const cell = tile.closest('.nh-bb__cell');
      const stage = stageSrc(plate, this.viewCap);
      if (cell) cell.hidden = !stage;
      if (!stage || (plate && plate.art)) return;
      const face = tile.querySelector('[data-nh-bb-tile-face]');
      const src = thumbSrc(plate, this.viewCap);
      if (face && src) face.style.setProperty('--nh-bb-tile-img', `url("${cssUrl(src)}")`);
    });
  }

  /**
   * The price line under the title.
   *
   * Rendered server-side AND re-rendered here from the same template, so it is
   * right with JavaScript off and stays right when the colourway changes.
   */
  paintSubline(cap) {
    const el = this.els.subline;
    if (!el) return;
    const tpl = el.getAttribute('data-nh-bb-subline-template') || '';
    if (!tpl) return;
    el.textContent = fill(tpl, { plates: this.max, price: (cap && cap.price) || '' });
  }

  /** "Your plates — choose any 3", once, from the tray title setting. */
  /* ROUND 19 — the GALLERY STRIP moved to assets/nordenheim-bundle-core.js
     (shotsFor / renderStrip / markStrip / openShot). Mix & Match declares
     `openShot` in `owns` and keeps its own — see the core's note there — and
     runs the other three. Moved unchanged. */

  /* ---------------------------------------------------------------- stage */

  /** What the stage should show right now. */
  currentStageSrc() {
    const plate = this.previewedPlate();
    if (this.shotIndex >= 0) {
      const shot = this.shotsFor(plate)[this.shotIndex];
      if (shot && shot.full) return shot.full;
    }
    const src = stageSrc(plate, this.viewCap);
    if (src) return src;
    const cap = this.cap(this.viewCap);
    return (cap && cap.capImage) || '';
  }

  /**
   * Paint the stage from current state.
   *
   *   snap — the plate arrived. The incoming layer is CLIPPED TO THE PLATE, so
   *          it may only ever be used when the cap underneath is unchanged.
   *   fade — the colourway changed. The whole picture is different, and a
   *          plate-shaped clip would leave the old cap showing around it for
   *          the length of the animation.
   */
  paintStage({ snap = false, fade = false } = {}) {
    const src = this.currentStageSrc();
    const plate = this.previewedPlate();
    this.paintCaption(plate);
    this.markStrip();
    // An open photograph is not a cap composite and must not take the composite's
    // crop. `is-shot` takes it off that crop; ROUND 13 splits what happens next,
    // because the two kinds of photograph in this array want opposite things:
    //
    //   editorial / worn — a photograph OF SOMEBODY. It fills the frame like any
    //     photo crop (`cover`), anchored HIGH so the cap and the face stay in and
    //     the crop is taken off the bottom. `contain` letterboxed these against
    //     the cap's ground, which Sina read as a picture floating in a box.
    //   packaging and the artwork tile — flat product shots. Whole or nothing:
    //     they stay `contain`.
    //
    // The composite's crop constants are untouched, so there is still exactly one
    // of them and `derive()` cannot pick a different one.
    if (this.els.stage) {
      const st = this.els.stage;
      const shot = this.shotIndex >= 0
        ? this.shotsFor(this.previewedPlate())[this.shotIndex]
        : null;
      const worn = !!(shot && !shot.art && !shot.packaging);
      // ROUND 16 (Sina: "zoom it in so it covers its space"). `contain`
      // letterboxed the flat-lay packaging photo — 17% of bare grey ground
      // above and below a 2048x2048 square in a 1.2:1 box. Cropped like the
      // worn shots (`cover`) but centred, not anchored high: the sleeve sits
      // in the middle of the flat-lay and the 17% this crop removes is sleeve
      // margin, not product. Independent of `compact`/`grow` below — a
      // packaging shot never takes the photograph's own aspect ratio, on any
      // viewport (round 14's desktop no-crop rule is for worn shots only).
      st.classList.toggle('is-shot-pack', !!(shot && shot.packaging));
      // `this.compact` — the stage has been MOVED into the pinned 150px header —
      // not the breakpoint. A phone's own stage is a full-width box like the
      // desktop one and grows the same way; only the shallow header band crops,
      // because a band that grew to a square would be half the screen.
      const compact = !!this.compact;
      st.classList.toggle('is-shot', this.shotIndex >= 0);
      // ROUND 14, REPLACING ROUND 13's SINGLE ANCHOR. `50% 22%` read well on the
      // female model shot and cut the CHIN off both Bästkusten men: those
      // photographs fill the square top to bottom, so a 4:3 window has to lose
      // ~20% of the height and no anchor keeps crown, plate and chin at once.
      // One constant cannot serve every worn shot, so on DESKTOP the shot stops
      // being cropped at all — the box takes the photograph's own shape.
      //
      // The compact phone header keeps its fixed 150px band, because a band that
      // grew to a square would be half the screen; there the anchor moved to 30%,
      // which holds the plate and the eyes. A header strip is for orientation,
      // not for reading a face.
      st.classList.toggle('is-shot-fill', worn && compact);
      const grow = worn && !compact;
      if (grow) {
        // From the payload, never from a measured load: sizing the box after the
        // image arrives is a reflow every shopper sees. 1:1 is the fallback,
        // which is what all 318 editorial entries measured as anyway.
        const ar = (shot.w > 0 && shot.h > 0) ? shot.w / shot.h : 1;
        st.style.setProperty('--nh-bb-shot-ar', String(Number(ar.toFixed(4))));
      }
      const had = st.classList.contains('is-shot-ar');
      st.classList.toggle('is-shot-ar', grow);
      // The box's ratio is what derive() reads, and the ResizeObserver that
      // normally re-derives it fires a frame later — long enough for one snap to
      // land on a stale clip. Re-derive synchronously on the transition instead.
      if (had !== grow) this.paintGeometry();
    }
    if (!src) return;
    // Never snap into an open photograph: the incoming layer is clipped to the
    // plate rectangle, and a worn shot has no plate at those coordinates.
    if (snap && plate && this.shotIndex < 0) {
      this.snapTo(src, plate);
      return;
    }
    const base = this.els.base;
    if (fade) {
      base.classList.remove('is-fading');
      // Force a reflow so the class can be re-added and re-trigger the fade.
      void base.offsetWidth;
      base.classList.add('is-fading');
    }
    setLayerImg(base, src);
  }

  paintCaption(plate) {
    const el = this.els.caption;
    if (!el) return;
    el.textContent = plate ? plate.name || '' : this.labels.empty || '';
  }

  /**
   * Start a swap once the incoming image is actually ready to paint.
   *
   * A CSS animation on an element whose background is still downloading does
   * not visibly begin until the bytes arrive, which reads as "the click did
   * nothing". Decoding first means the snap always plays on a loaded image;
   * cached images decode synchronously, so the common case stays instant.
   */
  snapTo(src, plate) {
    if (!src) return;
    this.paintCaption(plate);
    const token = (this.swapSeq = (this.swapSeq || 0) + 1);
    let started = false;
    const start = () => {
      if (started || token !== this.swapSeq || !this.ready) return;
      started = true;
      this.beginSnap(src);
    };
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
    if (img.complete) {
      start();
      return;
    }
    img.decode().then(start, start);
    const t = setTimeout(start, 400);
    this.timers.add(t);
  }

  beginSnap(src) {
    const stage = this.els.stage;

    // Retarget an in-flight swap rather than queueing: rapid clicking should
    // land on the last thing clicked, not replay every step.
    if (this.pending) {
      this.pending.remove();
      this.pending = null;
      if (this.pendingSrc) setLayerImg(this.els.base, this.pendingSrc);
    }

    const layer = document.createElement('div');
    layer.className = 'nh-bb__layer nh-bb__layer--incoming';
    setLayerImg(layer, src);
    layer.setAttribute('aria-hidden', 'true');

    this.pending = layer;
    this.pendingSrc = src;

    // Runs at most once. Without the latch the fallback timer fires a second
    // time after a normal animationend and re-writes src, silently reverting
    // any colourway change made in the meantime.
    let committed = false;
    let guard = null;
    const commit = () => {
      if (committed) return;
      committed = true;
      if (guard !== null) {
        clearTimeout(guard);
        this.timers.delete(guard);
      }
      layer.remove();
      if (this.pending === layer) {
        setLayerImg(this.els.base, src);
        this.pending = null;
        this.pendingSrc = null;
      }
    };
    layer.addEventListener('animationend', commit, { once: true });
    // animationend never fires if the layer is removed or animations are off,
    // so a timer guarantees the base image still lands.
    guard = setTimeout(commit, 1200);
    this.timers.add(guard);

    stage.appendChild(layer);
  }

  /* ---------------------------------------------------------- mobile bar */

  /**
   * Below 901px the buy block is relocated into the sticky bar; above it, back
   * into the panel. MOVED, not cloned — a copy would mean two forms, two
   * buttons and two sets of hidden inputs to keep in step.
   */
  /**
   * Below 901px the tray and the buy block are relocated into the sticky bar;
   * above it, back into the panel. MOVED, not cloned — a copy would mean two
   * trays, two forms and two sets of hidden inputs to keep in step.
   *
   * The CAP selector deliberately stays in the panel. It sits between the two
   * blocks on desktop, which is the only reason they are two elements rather
   * than one, and it is not something you reach for while confirming.
   */
  moveBuy() {
    const { buy, bar, panel, trayBlock, capCombo } = this.els;
    if (!buy || !bar || !panel) return;
    // The tray and the button belong INSIDE the filled deck, not beside it: put
    // them back into the panel and they land outside the fill, one element too
    // high, and only after a resize back from mobile — the kind of re-order
    // nobody sees until a customer does.
    const home = this.els.deck || panel;
    const compact = this.mq && this.mq.matches;
    // Crossing the breakpoint with the phone's cap modal open would strand the
    // listbox inside a <dialog> the desktop layout never opens.
    if (capCombo && this.comboOpen(capCombo)) this.toggleCombo(capCombo, false);
    // Widening the window past 901px must put the picture back in the panel.
    if (!compact && this.compact) this.setCompactStage(false);
    if (compact) {
      if (trayBlock && trayBlock.parentElement !== bar) bar.appendChild(trayBlock);
      // ROUND 11 (Sina): the cap goes into the bar too, as the FIRST tile in the
      // row — cap, then the plates, which is the order the offer is written in
      // ("an Art Cap + 3 Art Plates"). It is the SAME element as the desktop
      // dropdown, moved rather than cloned, so there is one listbox, one set of
      // options and one piece of state. A copy would be a second control that
      // could disagree with the first about which cap is in the cart.
      //
      // firstElementChild, not appendChild: the tray head is still in the DOM
      // (visually hidden — it is the live region that announces the count) and
      // appending would put the cap after it.
      if (capCombo && capCombo.parentElement !== trayBlock && trayBlock) {
        trayBlock.insertBefore(capCombo, trayBlock.firstChild);
      }
      if (capCombo) capCombo.classList.add('nh-bb__combo--tile');
      if (buy.parentElement !== bar) bar.appendChild(buy);
      bar.hidden = false;
      bar.classList.add('nh-bb__bar--compact');
      buy.classList.add('nh-bb__buy--compact');
      if (trayBlock) trayBlock.classList.add('nh-bb__buy--compact');
    } else {
      // The cap goes back FIRST, because the tray is then placed before it —
      // put the tray back first and `insertBefore(trayBlock, capCombo)` would be
      // asked to insert before a node that is still inside the tray block.
      if (capCombo && capCombo.parentElement !== home) home.appendChild(capCombo);
      if (capCombo) capCombo.classList.remove('nh-bb__combo--tile');
      // insertBefore, not appendChild: the cap selector sits BETWEEN these two
      // blocks, so the tray has to land above it. Appending would put both
      // under it — a silent re-order that only shows up after a resize back
      // from mobile.
      if (trayBlock && trayBlock.parentElement !== home) {
        home.insertBefore(trayBlock, capCombo || null);
      }
      if (buy.parentElement !== home) {
        // Last inside the deck.
        home.appendChild(buy);
      }
      bar.hidden = true;
      bar.classList.remove('nh-bb__bar--compact');
      buy.classList.remove('nh-bb__buy--compact');
      if (trayBlock) trayBlock.classList.remove('nh-bb__buy--compact');
    }
    // ROUND 15: the Tray Sheet takes the bar, the stage, the caption, the strip
    // and the cap list below 901px, and hands every one of them back above it.
    this.placeSheet();
    this.syncCapTileLabel();
    this.sizeBarTiles();
  }

  /* ============================================================ tray sheet */

  /**
   * ROUND 15 — THE TRAY SHEET. Mix & Match's bottom sheet, moved onto the
   * bundle PDP (the shell, the CSS state machine, bindSheetDrag, the
   * pixel-valued parked offset, the move-not-clone place(), the debounced
   * resize net and bindStageSwipe are all plate-snap's, renamed), with one
   * substitution: the parked peek is the phone buy bar, which is already the
   * bundle's receipt.
   *
   * ROUND 18: the sheet's BEHAVIOUR now lives in assets/nordenheim-bundle-core.js
   * and is shared with Mix & Match, which grew the same dock. What is left here
   * is which of THIS surface's nodes move into it — bcSheetMove() below.
   */
  placeSheet() {
    return this.placeDock();
  }

  /**
   * WHICH NODES MOVE, for this surface. Everything around it — the root's
   * `nh-bb--sheet` class, the dock's `hidden`, the one-time bindSheet, the
   * first-placement park without a transition, the body's `inert` and the
   * re-derived clip — is the core's placeDock(), shared with Mix & Match.
   *
   * Below 901px, in `sheet` mode:
   *   - the REAL stage, the caption row and the gallery strip MOVE into the
   *     sheet's body (one stage, one set of layers, one snap animation);
   *   - the bar (tray + button, already moved by moveBuy) MOVES into the
   *     dock's foot, where it is the sheet's footer and its parked peek.
   * Above it, or in `band` mode, every one of them goes back where it came
   * from, in DOM order. It is idempotent — the resize net runs it twice.
   *
   * NOTHING IN HERE IS display:none WHILE IT IS MEASURED. The parked sheet is
   * translated off-screen, not hidden: the stage keeps a real box, so the
   * ResizeObserver and derive() read real numbers (README rounds 10/11 record
   * the garbage clip a display:none stage computes). The body is
   * `visibility: hidden` + inert while parked, which hides it from touch and
   * from the accessibility tree without taking its layout away.
   */
  bcSheetMove(active) {
    const { sheetStage, sheetCapline, sheetStrip, sheetFoot,
            bar, stage, capline, strip, view, capCombo, capModal } = this.els;
    const list = this.els.capList;

    if (active) {
      if (stage && sheetStage && stage.parentElement !== sheetStage) {
        // FIRST, before the arrows, so the arrows paint above the picture.
        sheetStage.insertBefore(stage, sheetStage.firstChild);
      }
      if (capline && sheetCapline && capline.parentElement !== sheetCapline) {
        sheetCapline.appendChild(capline);
      }
      if (strip && sheetStrip && strip.parentElement !== sheetStrip) sheetStrip.appendChild(strip);
      // ROUND 16 (Sina) — the "Your cap" grid no longer moves into the sheet.
      // `list` stays wherever the desktop/band layout keeps it (inside
      // capCombo, or inside the modal while that is open); the cap tile keeps
      // its ordinary listbox popup semantics and opens round 11's centred
      // `<dialog>` on a tap, same as `band` mode. See toggleCombo().
      if (bar && sheetFoot && bar.parentElement !== sheetFoot) sheetFoot.appendChild(bar);
      return;
    }

    // ---- not active: put everything back --------------------------------
    // ROUND 16 (perf) — LEAVING SHEET MODE (a resize past 901px, or a
    // `phone_mode` change) BEFORE THE SHEET WAS EVER OPENED left the strip
    // stuck `hidden` from renderStrip()'s defer gate: desktop has never
    // deferred its images, and nothing else re-evaluates `strip.hidden` on a
    // breakpoint crossing. Marking it "opened" here is correct in both
    // directions — a genuine open always does the same — and the hidden state
    // is corrected without a full rebuild (the chips already built are still
    // its children).
    if (strip && !this.sheetEverOpened) {
      this.sheetEverOpened = true;
      strip.hidden = !strip.childElementCount;
    }
    // Back in DOM order: the stage first in the viewing zone, then the caption
    // first in the bands and the strip directly after it.
    if (view && stage && stage.parentElement !== view) view.insertBefore(stage, view.firstChild);
    const bands = view && view.querySelector('.nh-bb__bands');
    if (bands) {
      if (capline && capline.parentElement !== bands) bands.insertBefore(capline, bands.firstChild);
      if (strip && strip.parentElement !== bands) {
        bands.insertBefore(strip, capline && capline.parentElement === bands ? capline.nextSibling : null);
      }
    }
    if (list) {
      // Back inside the combo, before the dialog, so the anchored popup works
      // unchanged — the same seat closeCapModal puts it back in.
      if (capCombo && list.parentElement !== capCombo) capCombo.insertBefore(list, capModal || null);
      list.hidden = true;
    }
    if (this.els.capBtn && this.capBtnPopup) {
      this.els.capBtn.setAttribute('aria-haspopup', this.capBtnPopup.haspopup);
      if (this.capBtnPopup.controls) {
        this.els.capBtn.setAttribute('aria-controls', this.capBtnPopup.controls);
      }
      this.els.capBtn.setAttribute('aria-expanded', 'false');
    }
    if (bar && this.barHome && bar.parentElement !== this.barHome) {
      this.barHome.insertBefore(bar, this.barNext);
    }
  }

  /** The element that carries `.nh-bb--sheet` and the JS-written --nh-bb-*
   *  pixels. On this surface it is the section itself. */
  bcRoot() {
    return this.closest('.nh-bb') || this;
  }

  /**
   * ROUND 15.1 — the cap grid is IN the Tab sequence. Every row of the listbox
   * ships `tabindex="-1"` (the desktop popup moves focus into it on open), so
   * moved into the sheet as an always-visible grid nothing could Tab to it.
   * Roving tabindex: the selected row — or the first — takes 0 while the sheet
   * is the layout; arrows still move between rows (onComboKeydown). Every row
   * goes back to -1 above 901px, which is the desktop markup unchanged.
   */
  syncCapRoving() {
    const list = this.els && this.els.capList;
    if (!list) return;
    const opts = Array.from(list.querySelectorAll('[role="option"]'));
    if (!opts.length) return;
    const current = this.sheetActive()
      ? (list.querySelector('[role="option"][aria-selected="true"]') || opts[0])
      : null;
    opts.forEach((o) => o.setAttribute('tabindex', o === current ? '0' : '-1'));
  }










  // revealSheetCap() — ROUND 16: REMOVED with the "Your cap" grid it scrolled
  // to. The cap tile now opens the same centred `<dialog>` as `band` mode
  // (toggleCombo / openCapModal already move focus onto the selected row).








  /* ------------------------------------------------------------ preloading */

  preload(src) {
    if (!src) return;
    this.seen = this.seen || new Set();
    if (this.seen.has(src)) return;
    this.seen.add(src);
    const img = new Image();
    img.decoding = 'async';
    img.src = src;
  }
}

/* THE SHARED HALF. Copied onto the prototype AFTER the class body, so `this`
   inside every core method is this element and nothing about the call sites
   above had to change. `owns` is empty: every method the core defines was
   MOVED out of this file, so there is nothing here left to clobber — and if
   one is ever re-added by hand, installBundleCore says so loudly instead of
   silently winning. */
if (typeof installBundleCore === 'function') {
  installBundleCore(NordenheimBundleBuilder.prototype);
} else {
  // eslint-disable-next-line no-console
  console.error('[nordenheim-bundle-builder] nordenheim-bundle-core.js did not load; the section is left as the server rendered it.');
}

// The section may appear twice on a page, which means two identical <script>
// elements; defining twice throws and aborts the rest of the file.
if (typeof installBundleCore === 'function' && !customElements.get(TAG)) {
  customElements.define(TAG, NordenheimBundleBuilder);
}

export { NordenheimBundleBuilder, derive };
